// Fallback rate used until the live fetch completes.
const FALLBACK_INR_PER_USD = 83;

const INDIAN_TIME_ZONES = new Set(['asia/kolkata', 'asia/calcutta']);

// Module-level cache — shared across all components in the session.
let _usdToInrRate = FALLBACK_INR_PER_USD;
let _rateFetchPromise = null;

/**
 * Fetches the live USD→INR exchange rate from the open.er-api.com free tier
 * (no API key required). Safe to call multiple times — reuses the same promise.
 * Returns a promise that resolves once the cache is ready (or the fallback stays
 * on network error).
 */
export const fetchExchangeRate = () => {
    if (_rateFetchPromise) return _rateFetchPromise;
    _rateFetchPromise = fetch('https://open.er-api.com/v6/latest/USD')
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
            const rate = data?.rates?.INR;
            if (typeof rate === 'number' && rate > 0) {
                _usdToInrRate = rate;
            }
        })
        .catch(() => {}); // silently keep fallback on network failure
    return _rateFetchPromise;
};

export const detectBrowserPricingCurrency = () => {
    if (typeof window === 'undefined' || typeof Intl === 'undefined') {
        return 'USD';
    }

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (INDIAN_TIME_ZONES.has(timeZone.toLowerCase())) {
        return 'INR';
    }

    if (typeof navigator !== 'undefined') {
        const locales = [navigator.language, ...(navigator.languages || [])]
            .filter(Boolean)
            .map((locale) => String(locale).trim().toLowerCase());

        if (locales.some((locale) => locale.endsWith('-in'))) {
            return 'INR';
        }
    }

    return 'USD';
};

export const resolveBillingCurrency = (country) => {
    if (country === 'India') {
        return 'INR';
    }

    if (country) {
        return 'USD';
    }

    return detectBrowserPricingCurrency();
};

/**
 * Rounds a raw USD amount to a "charm price":
 *  - < $100 → snap to nearest $10, then subtract $1  (e.g. $29, $69)
 *  - ≥ $100 → snap to nearest $100, then subtract $1 (e.g. $299, $499)
 */
const toCharmUsd = (rawUsd) => {
    if (rawUsd < 100) {
        const snapped = Math.round(rawUsd / 10) * 10;
        return Math.max(9, snapped - 1);
    }
    const snapped = Math.round(rawUsd / 100) * 100;
    return Math.max(99, snapped - 1);
};

export const formatBillingPrice = (paise, currency) => {
    if (!paise || paise <= 0) {
        return 'Free';
    }

    const rupees = paise / 100;

    if (currency === 'USD') {
        const rawUsd = (rupees / _usdToInrRate) * 1.2;
        const displayUsd = toCharmUsd(rawUsd);
        return `$${displayUsd.toLocaleString('en-US')}`;
    }

    return `₹${rupees.toLocaleString('en-IN')}`;
};